import { DateView, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FC, useState } from 'react';
import { Moment } from 'moment';
import { useController, useFormContext } from 'react-hook-form';
import { IconButton, InputAdornment } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import { TInputDatePicker } from './type';
import { useAppSelector } from '../../hooks/useAppStore';
import { getTokenDetails } from '../../store/authenticate/selectors';
import 'moment/dist/locale/es';
import { ReactComponent as TodayIcon } from '../../static/icons/ic_today.svg';

const InputDatePicker: FC<TInputDatePicker> = ({
  onChange,
  placeholder,
  minDate,
  maxDate,
  defaultValue,
  name,
  isRequired = false,
  onError,
  disabled = false,
  isFullWidth = true,
  views = ['year', 'month', 'day'],
  format = 'MMMM Do YYYY',
  size = 'small',
  sx,
}) => {
  const { control } = useFormContext();
  const tokenDetails = useAppSelector(getTokenDetails);
  const {
    field: { ref, ...pickerProps },
    formState: { errors },
  } = useController({
    control,
    name,
    rules: {
      required: isRequired,
    },
    defaultValue: defaultValue || '',
  });

  const [open, setOpen] = useState(false);

  const handleChange = (date: Moment | null) => {
    pickerProps.onChange(date);
    onChange(date);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      adapterLocale={tokenDetails?.settings?.ui?.locale || 'en'}
    >
      <DatePicker
        value={pickerProps.value}
        {...(minDate && { minDate })}
        {...(maxDate && { maxDate })}
        views={views as readonly DateView[]}
        onChange={handleChange}
        onError={onError}
        disabled={disabled}
        inputRef={ref}
        open={open}
        format={format}
        onClose={() => setOpen(false)}
        sx={sx}
        slotProps={{
          textField: {
            onClick: () => setOpen(true),
            fullWidth: isFullWidth,
            size,
            placeholder,
            error: !!errors[name],
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setOpen(true)} // Open the picker when icon is clicked
                    edge="end"
                  >
                    <SvgIcon component={TodayIcon} inheritViewBox />
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export { InputDatePicker };
